/**
 * useModelWithOverrides Hook
 *
 * Fetches AI model configuration including parameter schema and admin overrides.
 * Returns merged parameters ready for node settings panel rendering.
 *
 * Features:
 * - Fetches from existing useNodeModels (no new API needed)
 * - Merges parameterSchema + parameterOverrides
 * - Returns sorted, filtered parameters
 * - Caching handled by useNodeModels
 *
 * @example
 * ```tsx
 * const { model, mergedParams, isLoading } = useModelWithOverrides('image', 'flux', 'my-model-id');
 *
 * // Filter visible settings
 * const settingsParams = getVisibleSettingsParameters(mergedParams);
 *
 * // Get input handles
 * const inputParams = getInputParameters(mergedParams);
 * ```
 */

import { useEffect, useMemo } from 'react';
import { exposeLlmSettings, llmSettingsProperties, type AINodeType, type AiModelPurpose, type NodeModelConfig } from '@genieartinc/schemas';

/**
 * Extract input properties from a parameterSchema.
 * The schema may be stored as:
 *   a) A flat properties object: { prompt: {...}, seed: {...} }
 *   b) A JSON Schema with top-level properties: { properties: { prompt: {...} } }
 *   c) A full OpenAPI spec: { components: { schemas: { <InputName>: { properties: {...} } } }, paths: {...} }
 * This function normalizes all cases to the flat properties object.
 */
function extractInputProperties(
  schema: Record<string, unknown>
): Record<string, Record<string, unknown>> {
  // Case (b): direct properties object
  if (schema.properties && !schema.components && !schema.paths) {
    return schema.properties as Record<string, Record<string, unknown>>;
  }

  // Case (c): full OpenAPI spec — find the Input schema in components
  if (schema.components) {
    const components = schema.components as Record<string, unknown>;
    const schemas = components.schemas as Record<string, Record<string, unknown>> | undefined;
    if (schemas) {
      // Find the request body schema via paths, or fall back to *Input named schema
      const paths = schema.paths as Record<string, Record<string, unknown>> | undefined;
      if (paths) {
        const firstPath = Object.values(paths)[0];
        const post = firstPath?.post as Record<string, unknown> | undefined;
        const body = post?.requestBody as Record<string, unknown> | undefined;
        const content = body?.content as Record<string, Record<string, unknown>> | undefined;
        const jsonContent = content?.['application/json'] as Record<string, unknown> | undefined;
        const ref = jsonContent?.schema as Record<string, string> | undefined;
        if (ref?.$ref) {
          const refName = ref.$ref.split('/').pop();
          if (refName && schemas[refName]?.properties) {
            return schemas[refName].properties as Record<string, Record<string, unknown>>;
          }
        }
      }
      // Fallback: find first schema with "Input" in name
      for (const [name, def] of Object.entries(schemas)) {
        if (name.includes('Input') && def.properties) {
          return def.properties as Record<string, Record<string, unknown>>;
        }
      }
    }
  }

  // Case (a): assume flat properties (each key is a param name with type/description)
  // Heuristic: if any value has a "type" field, treat as flat properties
  const firstValue = Object.values(schema)[0] as Record<string, unknown> | undefined;
  if (firstValue && typeof firstValue === 'object' && 'type' in firstValue) {
    return schema as Record<string, Record<string, unknown>>;
  }

  return {};
}
import { useNodeModels, findModelById } from './useNodeModels';
import { useOpenRouterParams, prefetchOpenRouterMeta } from './useOpenRouterParams';
import { buildParameterSchemaFromOpenRouter } from '@/lib/ai/openrouter-params';
import {
  mergeParametersWithAdminOverride,
  getVisibleSettingsParameters,
  getInputParameters,
  getOutputParameters,
  type MergedParameter,
} from '@/lib/ai/parameter-merge';

export interface UseModelWithOverridesResult {
  // Model data
  model: NodeModelConfig | null;
  allModels: NodeModelConfig[]; // All models for this nodeType/purpose (for picker)
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;

  // Merged parameters
  mergedParams: MergedParameter[];

  // Filtered parameters (convenience)
  settingsParams: MergedParameter[]; // Visible settings (not hidden, not input/output)
  inputParams: MergedParameter[]; // Input handles
  outputParams: MergedParameter[]; // Output handles
}

/**
 * Fetch model with parameter overrides and merge them
 *
 * @param nodeType - Node type (image, video, text, etc.)
 * @param modelFamilySlug - Model family slug (flux, stable-diffusion, etc.)
 * @param modelId - Model ID to fetch
 */
export function useModelWithOverrides(
  nodeType: AINodeType,
  modelFamilySlug: string | undefined,
  modelId: string,
  purpose?: AiModelPurpose,
  subPurpose?: string,
  enabled = true
): UseModelWithOverridesResult {
  // Fetch models list (includes parameterSchema + parameterOverrides)
  const { data: models, isLoading, error, refetch } = useNodeModels(nodeType, modelFamilySlug, purpose, subPurpose, enabled);

  // Find selected model
  const model = useMemo(() => {
    if (!modelId || models.length === 0) return null;
    return findModelById(models, modelId);
  }, [models, modelId]);

  // For text nodes, fetch OpenRouter model metadata (supported_parameters, defaults, limits)
  // Eagerly prefetch the blob in parallel with model loading (blob is model-independent)
  const isTextNode = enabled && nodeType === 'text-llm';
  useEffect(() => { if (isTextNode) prefetchOpenRouterMeta(); }, [isTextNode]);
  // Use endpointId (e.g., "openai/gpt-4o") for OpenRouter lookup, not DB id (short hash)
  const { meta: openRouterMeta, isLoading: orLoading } = useOpenRouterParams(isTextNode ? model?.endpointId : undefined);

  // Merge parameters
  const mergedParams = useMemo(() => {
    // For text nodes: the live OpenRouter list when this endpoint published one,
    // otherwise the versioned catalog (including Genie's unpublished seed ids).
    if (isTextNode && openRouterMeta) {
      const orSchema = buildParameterSchemaFromOpenRouter(openRouterMeta, model?.endpointId || modelId);
      return mergeParametersWithAdminOverride(
        orSchema.properties as Record<string, Record<string, unknown>>,
        model?.parameterOverrides || {}
      );
    }

    if (isTextNode && !orLoading) {
      const exposure = exposeLlmSettings({
        provider: model?.provider || 'openrouter',
        modelId: model?.endpointId || modelId,
      });
      if (exposure.controls.length > 0) {
        return mergeParametersWithAdminOverride(
          llmSettingsProperties(exposure),
          model?.parameterOverrides || {}
        );
      }
    }

    // For non-text nodes: use existing DB parameterSchema
    if (!model || !model.parameterSchema) {
      return [];
    }

    const schema = model.parameterSchema as Record<string, unknown>;

    // Array-format schema (transformed by admin sync pipeline): { inputs: [...], outputs: [...], settings: [...] }
    // Convert directly to MergedParameter[] without re-extracting from OpenAPI
    if (Array.isArray(schema.settings)) {
      const overrides = (model.parameterOverrides || {}) as Record<string, Record<string, unknown>>;
      const settings = schema.settings as Array<{
        name: string; type: string; component: string; group: string;
        default?: unknown; minimum?: number; maximum?: number; step?: number;
        enum?: unknown[]; description?: string; order?: number;
      }>;
      const inputs = (schema.inputs || []) as Array<{
        name: string; type: string; handleType: string; required?: boolean;
        order?: number; description?: string;
      }>;
      const outputs = (schema.outputs || []) as Array<{
        name: string; type: string; handleType: string; order?: number;
      }>;

      const merged: MergedParameter[] = [];

      // Map inputs to MergedParameter
      for (const inp of inputs) {
        merged.push({
          name: inp.name,
          label: inp.name,
          description: inp.description,
          type: inp.type || 'string',
          group: 'input',
          uiType: inp.handleType === 'text' ? 'textarea' : 'input',
          inputType: inp.handleType,
          required: inp.required,
          order: inp.order ?? 0,
        });
      }

      // Map settings to MergedParameter
      for (const s of settings) {
        const override = overrides[s.name];
        if (override?.visible === false) continue;

        merged.push({
          name: s.name,
          label: (override?.label as string) ?? s.name,
          description: (override?.description as string) ?? s.description,
          type: s.type || 'string',
          group: (override?.group as string) ?? s.group ?? 'advanced',
          uiType: (override?.uiTypeOverride as string) ?? s.component,
          default: override?.default ?? s.default,
          minimum: (override?.minimum as number) ?? s.minimum,
          maximum: (override?.maximum as number) ?? s.maximum,
          enum: (override?.enumOverride as unknown[]) ?? s.enum,
          order: (override?.order as number) ?? s.order ?? 99,
        });
      }

      // Map outputs to MergedParameter
      for (const out of outputs) {
        merged.push({
          name: out.name,
          label: out.name,
          type: out.type || 'string',
          group: 'output',
          uiType: 'output',
          outputType: out.handleType,
          order: out.order ?? 0,
        });
      }

      return merged;
    }

    // Legacy: raw OpenAPI schema — use old extraction + merge path
    const inputProps = extractInputProperties(schema);
    return mergeParametersWithAdminOverride(
      inputProps,
      model.parameterOverrides || {}
    );
  }, [model, isTextNode, openRouterMeta, orLoading, modelId]);

  // Filtered parameters (convenience)
  const settingsParams = useMemo(
    () => getVisibleSettingsParameters(mergedParams),
    [mergedParams]
  );

  const inputParams = useMemo(
    () => getInputParameters(mergedParams),
    [mergedParams]
  );

  const outputParams = useMemo(
    () => getOutputParameters(mergedParams),
    [mergedParams]
  );

  return {
    model,
    allModels: models,
    isLoading: isLoading || (isTextNode && orLoading),
    error,
    refetch,
    mergedParams,
    settingsParams,
    inputParams,
    outputParams,
  };
}
